/**
 * Singleton SSE client for query invalidation events.
 *
 * Connects to /api/v1/events when `connect()` is called (after login).
 * Reconnects with jittered exponential backoff on errors. Batches the
 * invalidations of each 250 ms window (one per `model:id`) to absorb bursts;
 * a fixed window, so a steady stream of events still gets through.
 */

import { useSyncExternalStore } from "react";
import type { InvalidatableModel } from "../api/queryKeys";

export interface InvalidationEvent {
  model: InvalidatableModel;
  /** Null when many rows of the model changed at once (the server collapsed them). */
  id?: string | null;
  tenant_id?: string | null;
  org_id?: string | null;
  user_ids?: string[] | null;
  op?: "upsert" | "delete" | "resync";
}

/** "down" from an error until the next successful open. */
export type RealtimeStatus = "connecting" | "open" | "down";

type InvalidateCallback = (events: InvalidationEvent[]) => void;
type Callback = () => void;

const BATCH_MS = 250;
const RECONNECT_INITIAL_MS = 1000;
const RECONNECT_MAX_MS = 30_000;

class SseClient {
  private es: EventSource | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = RECONNECT_INITIAL_MS;
  private batchTimer: ReturnType<typeof setTimeout> | null = null;
  private batch = new Map<string, InvalidationEvent>();
  private invalidateListeners = new Set<InvalidateCallback>();
  private reconnectListeners = new Set<Callback>();
  private unauthorizedListeners = new Set<Callback>();
  private statusListeners = new Set<Callback>();
  private status: RealtimeStatus = "connecting";
  private connected = false;
  private url: string;

  constructor(url = "/api/v1/events") {
    this.url = url;
  }

  connect(): void {
    if (this.connected) return;
    this.connected = true;
    this.setStatus("connecting");
    this.open();
  }

  disconnect(): void {
    this.connected = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.batchTimer) clearTimeout(this.batchTimer);
    this.batchTimer = null;
    this.batch.clear();
    if (this.es) {
      this.es.close();
      this.es = null;
    }
    this.setStatus("connecting");
  }

  onInvalidate(cb: InvalidateCallback): () => void {
    this.invalidateListeners.add(cb);
    return () => this.invalidateListeners.delete(cb);
  }

  onReconnect(cb: Callback): () => void {
    this.reconnectListeners.add(cb);
    return () => this.reconnectListeners.delete(cb);
  }

  /** The server ended the stream because the login is no longer valid. */
  onUnauthorized(cb: Callback): () => void {
    this.unauthorizedListeners.add(cb);
    return () => this.unauthorizedListeners.delete(cb);
  }

  subscribeStatus = (cb: Callback): (() => void) => {
    this.statusListeners.add(cb);
    return () => this.statusListeners.delete(cb);
  };

  getStatus = (): RealtimeStatus => this.status;

  private setStatus(status: RealtimeStatus): void {
    if (status === this.status) return;
    this.status = status;
    for (const cb of this.statusListeners) cb();
  }

  private open(): void {
    if (!this.connected) return;
    try {
      this.es = new EventSource(this.url, { withCredentials: true });
    } catch (err) {
      console.warn("[sse] failed to construct EventSource", err);
      this.setStatus("down");
      this.scheduleReconnect();
      return;
    }

    this.es.addEventListener("open", () => {
      this.reconnectDelay = RECONNECT_INITIAL_MS;
      this.setStatus("open");
    });

    this.es.addEventListener("reconnect", () => {
      for (const cb of this.reconnectListeners) cb();
    });

    this.es.addEventListener("unauthorized", () => {
      // The server closes the stream next; don't come back.
      this.disconnect();
      for (const cb of this.unauthorizedListeners) cb();
    });

    this.es.addEventListener("invalidate", (e) => {
      const ev = e as MessageEvent<string>;
      let evt: InvalidationEvent;
      try {
        evt = JSON.parse(ev.data);
      } catch {
        return;
      }
      this.enqueue(evt);
    });

    this.es.addEventListener("error", () => {
      if (this.es) {
        this.es.close();
        this.es = null;
      }
      this.setStatus("down");
      this.scheduleReconnect();
    });
  }

  private scheduleReconnect(): void {
    if (!this.connected) return;
    if (this.reconnectTimer) return;
    // ±50 % so a deploy's dropped clients don't all come back at once.
    const delay = this.reconnectDelay * (0.5 + Math.random());
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, RECONNECT_MAX_MS);
  }

  private enqueue(evt: InvalidationEvent): void {
    this.batch.set(`${evt.model}:${evt.id ?? "*"}`, evt);
    if (this.batchTimer) return;
    this.batchTimer = setTimeout(() => {
      this.batchTimer = null;
      const events = [...this.batch.values()];
      this.batch.clear();
      for (const cb of this.invalidateListeners) cb(events);
    }, BATCH_MS);
  }
}

export const sseClient = new SseClient();

/** Whether the realtime connection is up ("open"), coming up, or down. */
export function useRealtimeStatus(): RealtimeStatus {
  return useSyncExternalStore(sseClient.subscribeStatus, sseClient.getStatus);
}
